import { useEffect, useMemo, useRef, useState, forwardRef } from "react";

import { AgGridReact } from "ag-grid-react";
import Box from "@mui/material/Box"
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';

import { useDispatch } from "react-redux";
import { withTranslation } from "react-i18next";
import { isEmpty, isEqual, isNull, isUndefined } from "lodash";

import maxProductionModelService from "@plannel/services/data/max-production-model-service";
import resourceService from "@plannel/services/data/resource-service";
import workcenterService from "@plannel/services/data/workcenter-service";
import AutocompleteSearch from "@plannel/components/AutocompleteSearch";
import { MPLocationTreeFilter } from "@plannel/components/filter/LocationTreeFilter";
import PaginationContainer from "@plannel/components/PaginationContainer";
import Snackbar from "@plannel/components/Snackbar";
import Dialog from "@plannel/components/Dialog";
import ExcelExportButton from "@plannel/components/ExcelExportButton";
import GridDatePicker from "@plannel/components/GridDatePicker";
import DataState from "@plannel/components/aggrid/DataState";
import DefaultGridSetting, { gridPaste, nullSafeComparator } from "@plannel/components/aggrid/DefaultGridSetting";
import GridUtils from "@plannel/components/aggrid/GridUtils";
import { AddButton, RemoveButton, SaveButton, FilterButton } from "@plannel/components/ActionIconButton";
import AdvancedFilter from "@plannel/components/filter/AdvancedFilter";
import ResizableFilterPanel from "@plannel/components/filter/ResizableFilterPanel";
import FilterContainer from "@plannel/components/layout/FilterContainer";
import reduxUtil from "@plannel/utils/redux-util";
import debug from "@plannel/utils/debug-util";
import DateUtils from "@plannel/utils/date-util";
import ColorUtil from "@plannel/pages/utils/color-util";

const { snackBarMessageColor } = ColorUtil;

const MaxProductionModel = ({ t, viewName, title }) => {
  const reduxViewState = reduxUtil.getViewState(viewName);
  const reduxDispatch = useDispatch();

  const pageSizes = [100, 200, 500];
  const [currentPage, setCurrentPage] = useState(reduxViewState ? reduxViewState.currentPage : 1);
  const [tPages, setTotalPages] = useState(0);
  const [pageSize, setPageSize] = useState(reduxViewState ? reduxViewState.pageSize : pageSizes[0]);
  const [sortParams, setSortParams] = useState({});

  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });
  const [dialogInfo, setDialogInfo] = useState({ open: false, title: "", content: "" });

  const gridRef = useRef();
  const [gridLoading, setGridLoading] = useState(false);

  const [locationFilter, setLocationFilter] = useState(reduxViewState?.locationFilter ?? { locations: null });
  const [workcenterSearch, setWorkcenterSearch] = useState(reduxViewState?.workcenterSearch ?? null);
  const [resourceSearch, setResourceSearch] = useState(reduxViewState?.resourceSearch ?? null);
  const [advancedFilters, setAdvancedFilters] = useState(reduxViewState?.advancedFilters ? reduxViewState.advancedFilters : null);
  const [openFilter, setOpenFilter] = useState(false);
  const [advancedFilterColor, setAdvancedFilterColor] = useState(!isEmpty(reduxViewState?.advancedFilters) ? "info" : "default");

  const [maxProductionModels, setMaxProductionModels] = useState([]);
  const [workcenterOptions, setWorkcenterOptions] = useState([]);
  const [resource, setResource] = useState([]);
  const resourceMap = useRef({});

  const defaultGridMemo = useMemo(() => {
    return DefaultGridSetting({ title, viewName })

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const DynamicGridDatePicker = forwardRef((props, ref) => {
    const dynamicParams = {
      dateType: props.dateType,
      maxDate: props.maxDate,
      minDate: props.minDate,
    };

    const mockedProps = {
      ...props,
      column: {
        ...props.column,
        colDef: {
          ...props.column.colDef,
          cellEditorParams: dynamicParams
        }
      }
    };

    return <GridDatePicker {...mockedProps} ref={ref} />;
  });

  const dropdownResourceValueFormatter = (data) => {
    if (typeof data?.value === "string") {
      return data.value;
    }

    return data.value ? data.value.resCd : "";
  }

  const objectComparator = (value1, value2, property, isDescending) => {
    const val1 = !value1 ? "" : value1[property];
    const val2 = !value2 ? "" : value2[property];

    return nullSafeComparator(val1, val2, isDescending);
  }

  const checkInputValidation = (params) => {
    const colId = params.column.colId;

    if (isNaN(params.newValue) || isNull(params.newValue) || isUndefined(params.newValue)) {
      params.data[colId] = "";

      return false;
    }

    if (params.newValue < 0) {
      setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: t("MSG_InvalidValue") });
      return false;
    }

    if (!Number.isInteger(Number(params.newValue))) {
      setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: t("MSG_InvalidValue") });
      return false;
    }

    params.data[colId] = params.newValue;

    return true;
  }

  const onCellValueChanged = (params) => {
    gridPaste(params);

    const { column, node } = params;
    const colId = column.getColId();

    if (isEqual(colId, "resCd")) {
      const selected = resourceMap.current[params.value];
      if (!selected) {
        node.data.resId = null;
        node.data.resCd = null;
        node.data.resName = null;
        node.data.locCd = null;
        node.data.locName = null;
        node.data.wcCd = null;
        setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: `[${t("resource")}] ${t("MSG_InvalidValue")}` });
        return;
      }

      node.data.resId = selected.id;
      node.data.resCd = selected.resCd;
      node.data.resName = selected.name;
      node.data.locCd = selected.locCd;
      node.data.locName = selected.locName;
      node.data.wcCd = selected.workcenterCd;
      params.api.refreshCells({ rowNodes: [node], columns: ["locCd", "locName", "wcCd", "resName"], force: true });
    }

    DataState.setDataState(params);
  }

  const columnDefs = [
    {
      headerName: "locCd", field: "locCd", colId: "locCd",
      type: ["nonEditableColumn"],
      cellClass: 'stringType',
      width: 180, filterType: 'string'
    },
    {
      headerName: "locName", field: "locName", colId: "locName",
      type: ["nonEditableColumn"],
      width: 180, filterType: 'string'
    },
    {
      headerName: "wcCd", field: "wcCd", colId: "wcCd",
      type: ["nonEditableColumn"],
      cellClass: 'stringType',
      width: 180, filterType: 'string'
    },
    {
      headerName: "resCd", field: "resCd", colId: "resCd",
      cellEditor: "agRichSelectCellEditor",
      cellEditorPopup: true,
      type: ["defaultValueParser"],
      cellClass: 'stringType',
      valueFormatter: dropdownResourceValueFormatter,
      cellEditorParams: {
        valueFormatter: dropdownResourceValueFormatter,
        values: reduxViewState?.resource ?? resource
      },
      comparator: (value1, value2, nodeA, nodeB, isDescending) => objectComparator(value1, value2, "resCd", isDescending),
      width: 180, filterType: 'string'
    },
    {
      headerName: "resName", field: "resName", colId: "resName",
      type: ["nonEditableColumn"],
      width: 180, filterType: 'string'
    },
    {
      headerName: "startDate", field: "startDate", colId: "startDate",
      type: ["defaultValueParser"],
      cellEditor: DynamicGridDatePicker,
      cellEditorParams: (params) => ({ dateType: "day", maxDate: params.data.endDate ? new Date(params.data.endDate) : null }),
      valueFormatter: (data) => DateUtils.formatDate(data.value, "yyyy-MM-dd"),
      width: 120, filterType: 'date'
    },
    {
      headerName: "endDate", field: "endDate", colId: "endDate",
      type: ["defaultValueParser"],
      cellEditor: DynamicGridDatePicker,
      cellEditorParams: (params) => ({ dateType: "day", minDate: params.data.startDate ? new Date(params.data.startDate) : null }),
      valueFormatter: (data) => DateUtils.formatDate(data.value, "yyyy-MM-dd"),
      width: 120, filterType: 'date'
    },
    {
      headerName: "maxModelCount", field: "maxModelCount", colId: "maxModelCount",
      type: ["numberColumn", "rightAligned"],
      valueSetter: (params) => checkInputValidation(params),
      width: 150, filterType: 'number'
    },
    { headerName: "activeFlg", field: "activeFlg", type: ["booleanColumn"], width: 100, filterType: 'boolean' },
    { headerName: "createdTs", field: "createdTs", valueFormatter: DateUtils.formatDateTime, type: "nonEditableColumn", filterType: 'timestamp' },
    { headerName: "createdBy", field: "createdUser", colId: "createdBy", type: "nonEditableColumn", width: 150, filterType: 'string' },
    { headerName: "updatedTs", field: "updatedTs", valueFormatter: DateUtils.formatDateTime, type: "nonEditableColumn", filterType: 'timestamp' },
    { headerName: "updatedBy", field: "updatedUser", colId: "updatedBy", type: "nonEditableColumn", width: 150, filterType: 'string' },
    { field: "verNum", hide: true }
  ];

  const getRequestParams = () => {
    const params = {};
    const searchFilters = {};

    if (locationFilter?.locations?.length > 0) {
      searchFilters.locationCd = locationFilter.locations.map(l => l.locCd);
    }

    if (workcenterSearch) {
      searchFilters.workCenterCd = workcenterSearch.wcCd;
    }

    if (resourceSearch) {
      searchFilters.resCd = resourceSearch.resCd;
    }

    if (!isEmpty(advancedFilters) && advancedFilters?.children.length > 0) {
      params.advancedFilters = advancedFilters;
    }

    if (currentPage) {
      params.page = currentPage - 1;
    }

    if (pageSize) {
      params.pageSize = pageSize;
    }

    params.searchFilters = searchFilters;

    return { ...params, ...sortParams };
  }

  const retrieveWorkcenter = () => {
    workcenterService.getAll({ pagination: false })
      .then(response => {
        const { results } = response.data;
        setWorkcenterOptions(results ?? []);
      })
      .catch(e => {
        debug(e);
      });
  }

  const retrieveResource = () => {
    resourceService.getAll()
      .then(response => {
        const { results } = response.data;
        const resource = [];
        for (const res of results) {
          resource.push(res.resCd);
          resourceMap.current[res.resCd] = res;
        }
        setResource(resource);
      })
      .catch(e => {
        debug(e);
      });
  }

  const retrieveMaxProductionModels = () => {
    const params = getRequestParams();

    maxProductionModelService.getAll(params)
      .then(response => {
        const { results, totalPages } = response.data;

        if (isEmpty(results)) {
          setMaxProductionModels([]);
          setSnackInfo({ open: true, severity: snackBarMessageColor.dataEmpty, content: t("MSG_NoData") });
        }

        setMaxProductionModels(results);
        setTotalPages(totalPages);
      })
      .catch(e => {
        debug(e);
      })
      .then(() => {
        const reduxState = reduxUtil.createObjectState(viewName, {
          locationFilter, workcenterSearch, resourceSearch, currentPage, pageSize, advancedFilters
        });

        reduxDispatch(reduxUtil.updateViewState(reduxState));
        DataState.initialize(gridRef.current?.api);
      });
  }

  const onGridReady = params => {
    retrieveWorkcenter();
    retrieveResource();
    setGridLoading(true);
    GridUtils.setColumnDefs({ ...params, columnDefs, viewName, initState: true });
  }

  const addRow = () => {
    const newRow = {
      id: null,
      locCd: null,
      locName: null,
      wcCd: null,
      resId: null,
      resCd: null,
      resName: null,
      startDate: null,
      endDate: null,
      maxModelCount: null,
      activeFlg: true,
      verNum: null
    };

    DataState.setDataState(GridUtils.addRow(gridRef.current.api, newRow));
  }

  const removeMaxProductionModel = () => {
    const maxProductionModelIds = [];

    gridRef.current.api.stopEditing();
    gridRef.current.api.getSelectedRows().forEach(
      (data) => {
        maxProductionModelIds.push(data.id)
      }
    );

    if (maxProductionModelIds.length > 0) {
      maxProductionModelService.remove(maxProductionModelIds)
        .then(() => {
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_DeleteSuccess") });
        })
        .catch(e => {
          const { message } = e.response?.data ?? {};
          if (message) {
            setSnackInfo({ open: true, severity: message.type, content: t(message.message) });
          } else {
            setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_DeleteFailed") });
          }
          debug(e);
        })
        .then(() => {
          retrieveMaxProductionModels();
        });
    } else {
      setSnackInfo({ open: true, severity: snackBarMessageColor.noSaveData, content: t("MSG_NoSaveData") });
    }
  }

  const saveMaxProductionModel = () => {
    gridRef.current.api.stopEditing();
    const allStateData = DataState.getAllStateData(gridRef.current.api);

    if (allStateData.length === 0) {
      setSnackInfo({ open: true, severity: snackBarMessageColor.noSaveData, content: t("MSG_NoSaveData") });
      return;
    }

    for (const row of allStateData) {
      if (isEmpty(row.resCd)) {
        setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: `[${t("resource")}] ${t("MSG_FieldRequired")}` });
        return;
      }

      if (!row.startDate) {
        setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: `[${t("startDate")}] ${t("MSG_FieldRequired")}` });
        return;
      }

      if (!row.endDate) {
        setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: `[${t("endDate")}] ${t("MSG_FieldRequired")}` });
        return;
      }

      if (row.endDate < row.startDate) {
        setSnackInfo({ open: true, severity: snackBarMessageColor.noMatchCondition, content: t("MSG_SetEndDate") });
        return;
      }

      if (isNull(row.maxModelCount) || row.maxModelCount === "") {
        setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: `[${t("maxModelCount")}] ${t("MSG_FieldRequired")}` });
        return;
      }

      if (row.resCd) {
        row.resId = resourceMap.current[row.resCd]?.id;
      }
    }

    maxProductionModelService.upsert(allStateData)
      .then(() => {
        setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_SaveSuccess") });
      })
      .catch(e => {
        const { message } = e.response?.data ?? {};
        if (message) {
          setSnackInfo({ open: true, severity: message.type, content: t(message.message) });
        } else {
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_SaveFailed") });
        }
        debug(e);
      })
      .then(() => {
        retrieveMaxProductionModels();
      });
  }

  const handleDialog = (action) => {
    if (action === "SAVE") {
      saveMaxProductionModel();
    } else if (action === "DELETE") {
      removeMaxProductionModel();
    }
  }

  const initPageToRetrieve = () => {
    if (currentPage > 1) {
      setCurrentPage(1);
    } else {
      retrieveMaxProductionModels();
    }
  }

  const collapseFilter = _event => {
    setOpenFilter(!openFilter);
  };

  useEffect(() => {
    if (!isUndefined(resource) && !isEmpty(resource)) {
      GridUtils.setColumnDefs({ ...gridRef.current, columnDefs, viewName });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resource]);

  useEffect(() => {
    if (gridLoading) {
      DataState.initialize(gridRef.current?.api);
      retrieveMaxProductionModels();
    }

    return () => {
      setMaxProductionModels([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize, gridLoading, sortParams]);

  useEffect(() => {
    if (gridLoading) {
      initPageToRetrieve();
    }

    return () => {
      setMaxProductionModels([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [advancedFilters, locationFilter, workcenterSearch, resourceSearch]);

  useEffect(() => {
    if (!isEmpty(advancedFilters) && advancedFilters?.children.length > 0) {
      setAdvancedFilterColor("info");
    } else {
      setAdvancedFilterColor("default");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [advancedFilters]);

  return (
    <>
      <FilterContainer>
        <MPLocationTreeFilter
          viewName={viewName}
          filterValue={locationFilter?.locations?.map(l => l.locCd)}
          onChangeFilter={setLocationFilter}
        />
        <Autocomplete
          options={workcenterOptions}
          getOptionLabel={option => option.wcCd ?? ""}
          value={workcenterSearch}
          onChange={(_, newValue) => setWorkcenterSearch(newValue)}
          isOptionEqualToValue={(option, value) => option.wcCd === value.wcCd}
          size="small"
          sx={{ pr: 1, minWidth: 200 }}
          renderInput={(params) => (
            <TextField {...params} label={t("workcenter")} />
          )}
        />
        <AutocompleteSearch label="resource" value={resourceSearch} setValue={setResourceSearch}
          optionLabelProp="resCd" optionEqualProp="resCd" target="resource" />
        <Box sx={{ ml: "auto", mr: 1 }}>
          <ExcelExportButton title={title} service={maxProductionModelService} params={getRequestParams()} columnDefs={columnDefs} />
          <AddButton onClick={addRow} />
          <RemoveButton onClick={() => setDialogInfo({ open: true, title: "delete", content: t("MSG_DeleteConfirm") })} />
          <SaveButton onClick={() => setDialogInfo({ open: true, title: "save", content: t("MSG_SaveConfirm") })} />
          <FilterButton label="advancedFilter" advancedFilterColor={advancedFilterColor} onClick={collapseFilter} />
        </Box>
      </FilterContainer>
      <Box id="max-production-model-grid" className="ag-theme-balham grid-area" mb={1}>
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'row', height: '100%', mr: 1.5 }}>
          <Box sx={{ flexGrow: 1 }}>
            <AgGridReact
              ref={gridRef}
              rowData={maxProductionModels}
              {...defaultGridMemo}
              rowSelection={{
                ...defaultGridMemo.rowSelection,
                headerCheckbox: true,
                checkboxes: true,
              }}
              onGridReady={onGridReady}
              onCellValueChanged={onCellValueChanged}
              onSortChanged={params => setSortParams(GridUtils.getSortState(params))}
            />
          </Box>
          {openFilter && (
            <ResizableFilterPanel>
              <AdvancedFilter
                columnDefs={columnDefs}
                advancedFilters={advancedFilters}
                onApply={setAdvancedFilters}
                onClose={() => setOpenFilter(false)}
              />
            </ResizableFilterPanel>
          )}
        </Box>
      </Box>
      <PaginationContainer
        currentPage={currentPage} totalPages={tPages}
        pageSize={pageSize} pageSizes={pageSizes}
        setCurrentPage={setCurrentPage} setPageSize={setPageSize}
      />
      <Dialog
        open={dialogInfo.open}
        onClose={() => setDialogInfo({ open: false, title: "", content: "" })}
        title={dialogInfo.title}
        content={dialogInfo.content}
        onHandler={handleDialog}
      />
      <Snackbar
        open={snackInfo.open}
        onClose={() => setSnackInfo({ ...snackInfo, open: false })}
        severity={snackInfo.severity}
        content={snackInfo.content}
      />
    </>
  );
};

export default withTranslation()(MaxProductionModel);
